import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import {
  Card, LineChart, SectionHead, Steps,
} from '../../PipelineComponents';
import IntakeLogCard from './IntakeLogCard';
import IntakeFollowUpCard from './IntakeFollowUpCard';

const intakeSteps = [
  <>
    <b>Intake moves in.</b>
    {' '}
    Daily tracker imports as leads with a source - nothing is worked off a spreadsheet.
  </>,
  <>
    <b>Every stall gets a reason.</b>
    {' '}
    A pipeline record cannot be saved or closed without one.
  </>,
  <>
    <b>Every stage gets a timestamp.</b>
    {' '}
    Cycle time is then computed, not estimated.
  </>,
  <>
    <b>Every record gets an owner and an SLA.</b>
    {' '}
    Uncontacted past 24h and transfers past day 3 surface automatically.
  </>,
];

const SOURCE_TIPS = {
  health_plan_list: {
    text: 'Leads that came from a plan-supplied list.',
    steps: ['Anything with no source recorded also lands here.'],
    note: 'Because blanks fall into this bucket, a bar this dominant usually means the source is being skipped'
      + ' at intake rather than that the plan lists are this productive.',
  },
  inbound_call: {
    text: 'Members who called you.',
    steps: ['Logged when the lead is created from the call.'],
    note: 'Normally the highest-converting channel, because the member has already decided to act. Worth'
      + ' comparing its conversion rate against the plan lists before spending more anywhere else.',
  },
  daily_tracker: {
    text: 'Leads imported from the daily tracker.',
    steps: ['Arrives as a source on the lead rather than as a separate spreadsheet.'],
    note: 'This is the change that matters: the tracker living inside the system, where it can be counted'
      + ' alongside every other channel.',
  },
};

const Intake = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [leadsData, setLeadsData] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getLeadsSummary(params);

        setLeadsData(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const { totals, sources } = useMemo(() => {
    const stats = leadsData?.kpis;

    return {
      totals: [
        { label: 'New leads', value: stats?.newLeads ?? 0 },
        { label: 'Converted to SP', value: stats?.converted ?? 0 },
        {
          label: 'Conversion rate',
          value: stats?.conversionRate ? `${stats.conversionRate}%` : '0',
        },
        {
          label: 'Avg hours to first contact',
          value: stats?.avgHoursToFirstContact ?? 0,
        },
        { label: 'Uncontacted > 24h', value: stats?.uncontacted24h ?? 0 },
      ],

      sources: (leadsData?.bySource || []).map((d) => ({
        code: d.source,
        value: d.count || 0,
        tooltip: SOURCE_TIPS[d.source],
      })),
    };
  }, [leadsData]);

  return (
    <>
      <SectionHead section={section} />

      <div className={`${classes.row} ${classes.row_2u}`}>
        <IntakeLogCard params={params} />

        <Card
          title="Intake volume"
          caption="Leads received and converted in the period"
          loading={loading}
          firstLoad={firstLoad.current}
        >
          <div className={`${classes.kv_list} ${classes.bordered}`}>
            {totals.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.label}

                <span className={classes.kv_value}>{item.value}</span>
              </div>
            ))}
          </div>

          <LineChart data={sources} />
        </Card>
      </div>

      <div className={`${classes.row} ${classes.row_2}`}>
        <IntakeFollowUpCard params={params} />

        <Card
          title="Current process & cleanup opportunities"
          caption="A workflow answer, not a screen"
        >
          <p className={`${classes.note} ${classes.bordered}`}>
            <b>Today’s gaps</b>
            {' '}
            - the daily tracker lives in a spreadsheet outside SortPak; transfers are not tracked end to
            end; and no field records
            {' '}
            <i>why</i>
            {' '}
            a member is not filling, so no report can count it. Follow-up depends on whoever remembers.
          </p>

          <Steps items={intakeSteps} />
        </Card>
      </div>
    </>
  );
};

Intake.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default Intake;
